/**
 * POS PIN Pad Login Component
 * 6-digit PIN authentication for POS staff
 */
import { useState } from 'react';
import { Coffee, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function POSPinPad({ onLogin, loading }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleDigit = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      setError('');
      
      // Auto-submit when 6 digits entered
      if (newPin.length === 6) {
        handleSubmit(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleSubmit = async (pinCode) => {
    const result = await onLogin(pinCode);
    if (!result.success) {
      setError(result.error || 'Invalid PIN');
      setPin('');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-black dark:via-gray-900 dark:to-black p-4" data-testid="pos-login-screen">
      <div className="w-full max-w-md">
        {/* Logo Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-teal-600 rounded-2xl mb-4">
            <Coffee className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">POS Terminal</h1>
          <p className="text-gray-400">Enter your 6-digit PIN to sign in</p>
        </div>

        {/* PIN Display */}
        <div className="bg-gray-800/50 rounded-2xl p-6 mb-6 backdrop-blur">
          <div className="flex justify-center gap-3 mb-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${
                  pin.length > i
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-700 text-gray-500'
                }`}
              >
                {pin.length > i ? '•' : ''}
              </div>
            ))}
          </div>
          {error && (
            <div className="flex items-center justify-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </div>

        {/* Number Pad */}
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
            <Button
              key={digit}
              variant="outline"
              onClick={() => handleDigit(String(digit))}
              disabled={loading}
              className="h-16 text-2xl font-semibold bg-gray-800 border-gray-700 text-white hover:bg-gray-700 hover:text-white"
              data-testid={`pin-digit-${digit}`}
            >
              {digit}
            </Button>
          ))}
          <Button
            variant="outline"
            onClick={handleClear}
            disabled={loading}
            className="h-16 text-lg font-medium bg-red-900/30 border-red-800 text-red-400 hover:bg-red-900/50 hover:text-red-300"
            data-testid="pin-clear"
          >
            Clear
          </Button>
          <Button
            variant="outline"
            onClick={() => handleDigit('0')}
            disabled={loading}
            className="h-16 text-2xl font-semibold bg-gray-800 border-gray-700 text-white hover:bg-gray-700 hover:text-white"
            data-testid="pin-digit-0"
          >
            0
          </Button>
          <Button
            variant="outline"
            onClick={handleBackspace}
            disabled={loading}
            className="h-16 text-lg font-medium bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            data-testid="pin-backspace"
          >
            ←
          </Button>
        </div>

        {loading && (
          <div className="flex items-center justify-center mt-6 text-teal-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Signing in...
          </div>
        )}

        <p className="text-center text-gray-500 text-sm mt-8">
          Contact your manager if you forgot your PIN
        </p>
      </div>
    </div>
  );
}
